import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const faqs = [
  {
    question: 'Who provides ISNetworld compliance help near me?',
    answer:
      'Energy Contractor Services (ECS) provides expert ISNetworld compliance management for energy contractors across the United States and internationally. Call 405-408-1117 to get started. We offer quick turnaround for new accounts and ongoing maintenance to keep existing accounts at 100% compliance.',
  },
  {
    question: 'How much does ISNetworld compliance management cost?',
    answer:
      'ECS offers flexible month-to-month pricing with no long-term contracts required. You can cancel at any time. Contact us at 405-408-1117 for a quote tailored to your specific compliance needs and current account status across ISNetworld, Veriforce, or Avetta.',
  },
  {
    question: 'How long does it take to get ISNetworld approved?',
    answer:
      'With Energy Contractor Services, new ISNetworld account setups are completed with a quick turnaround. Existing accounts needing score improvements see results faster when managed by our specialists. Contact us at 405-408-1117 to discuss your specific situation and timeline.',
  },
  {
    question: 'Do I need to sign a long-term contract with ECS?',
    answer:
      'No. Energy Contractor Services operates on a month-to-month basis with absolutely no contracts required. You can cancel at any time with no penalties or long-term obligations. This flexible model means contractors get exactly the compliance help they need without any financial commitment beyond the current month.',
  },
  {
    question: 'Can ECS manage multiple platforms — ISNetworld, Veriforce, and Avetta?',
    answer:
      'Yes. Energy Contractor Services manages compliance for all three major contractor prequalification platforms simultaneously. Whether you need one or all three managed, you work with the same dedicated expert who tracks requirements, updates documentation, and ensures nothing lapses across any account.',
  },
  {
    question: 'What is RAVS Plus and how can ECS help?',
    answer:
      'RAVS Plus is an advanced documentation component of ISNetworld compliance required by many major energy operators. It\'s one of the most complex requirements to complete correctly. Energy Contractor Services provides specialized RAVS Plus support — preparing, submitting, and maintaining your documentation to meet the strictest operator standards.',
  },
  {
    question: 'What makes ECS different from other compliance services?',
    answer:
      'Three things: you work with the same dedicated expert every time (not a call center), 100% approval is guaranteed, and there are zero contracts — cancel anytime. ECS combines deep platform expertise with genuine accountability. We don\'t move on until your account is fully approved and maintained correctly.',
  },
  {
    question: 'Does ECS handle ongoing maintenance after getting approved?',
    answer:
      'Yes. Getting approved is step one — staying compliant long-term is what protects your contracts. ECS monitors your accounts monthly, tracks renewal requirements, updates documentation, and ensures your compliance never lapses. Month-to-month service means your accounts stay active and accurate without you having to manage the details.',
  },
  {
    question: 'How do I improve my ISNetworld score?',
    answer:
      'Energy Contractor Services specializes in improving ISNetworld account scores and grades. We review your current account, identify all gaps, and implement targeted improvements across documentation, safety training records, TRIR/DART/EMR rates, and RAVS compliance. Call 405-408-1117 to start improving your score today.',
  },
  {
    question: 'What states does Energy Contractor Services work in?',
    answer:
      'ECS serves contractors across all 50 US states and internationally, including Canada, Australia, the United Kingdom, Mexico, the Netherlands, New Zealand, and Dubai. Compliance management is handled remotely — wherever you work, we can help.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F0E8] py-24 px-6 lg:px-14">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
          {/* Left: Label + sticky header */}
          <div className="lg:col-span-1">
            <p className="overline mb-4">Voice Search</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line">
              Common Questions
            </h2>
            <p className="text-[#5A5A5A] text-sm font-light mt-8 leading-relaxed">
              Direct answers to the questions energy contractors ask most about ISNetworld compliance, Veriforce management, and Avetta qualification.
            </p>
            <div className="mt-10 border border-[#1A1409]/10 p-6">
              <p className="text-[#1A1409]/45 text-[10px] tracking-[0.25em] uppercase font-medium mb-3">
                Still have questions?
              </p>
              <a
                href="tel:4054081117"
                className="flex items-center gap-3 group cursor-pointer"
              >
                <div className="w-10 h-10 bg-[#C9A030] flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-[#0A0A0A] text-base" />
                </div>
                <div>
                  <p className="text-[#1A1409] font-semibold text-lg group-hover:text-[#C9A030] transition-colors">
                    405-408-1117
                  </p>
                  <p className="text-[#5A5A5A] text-xs font-light">Call us directly</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: Accordion */}
          <div
            ref={ref}
            className={`lg:col-span-2 fade-right ${isVisible ? 'visible' : ''}`}
          >
            {faqs.map((faq, idx) => (
              <div key={idx} className="border-b border-[#1A1409]/08">
                <button
                  className="w-full flex items-start justify-between py-5 text-left gap-6 cursor-pointer group"
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                >
                  <span
                    className={`font-medium text-[14px] leading-snug transition-colors duration-200 ${
                      openIndex === idx ? 'text-[#C9A030]' : 'text-[#1A1409] group-hover:text-[#C9A030]'
                    }`}
                  >
                    {faq.question}
                  </span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 border border-[#1A1409]/12 group-hover:border-[#C9A030]/40 transition-colors">
                    <i
                      className={`text-[#C9A030] text-sm transition-transform duration-300 ${
                        openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    />
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-400 ${
                    openIndex === idx ? 'max-h-96 pb-6' : 'max-h-0'
                  }`}
                >
                  <p className="text-[#5A5A5A] text-sm font-light leading-relaxed pr-10">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
